import React, {useCallback, useMemo, useState} from 'react';
import {EventPosition} from '@/types/matrix';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import {useTranslation} from 'react-i18next';
import MessageChipComponent from './text-message-chip';
import {MatrixEvent} from 'matrix-js-sdk';
import Reply from './reply-text-message-chip';
import {useMatrixRoomContext} from '@/contexts/matrix/room';

interface UserMatrixEventProps {
  matrixEvent: MatrixEvent;
  eventPosition: EventPosition;
}

const UserMatrixEvent: React.FC<UserMatrixEventProps> = ({
  matrixEvent,
  eventPosition
}) => {
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const {t} = useTranslation();
  const {setMatrixEventToQuote, activeMatrixEvent, handleDeleteMatrixEvent} =
    useMatrixRoomContext();

  const matrixEffectiveEvent = matrixEvent.getEffectiveEvent();

  const messageContent = matrixEffectiveEvent.content.body || '';

  const handleOpenDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(true);
  }, []);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleDeleteClick = useCallback(async () => {
    try {
      await handleDeleteMatrixEvent(matrixEvent);
      handleCloseDeleteConfirmation();
    } catch (error) {
      console.error('Failed to redact the message:', error);
    }
  }, [handleDeleteMatrixEvent, matrixEvent, handleCloseDeleteConfirmation]);

  const handleSetQuotedMessage = useCallback(() => {
    setMatrixEventToQuote(activeMatrixEvent);
  }, [activeMatrixEvent, setMatrixEventToQuote]);

  const isReply = useMemo(() => {
    return (
      messageContent.includes('[[QUOTE id=') &&
      messageContent.includes('[[/QUOTE]]')
    );
  }, [messageContent]);

  return (
    <>
      {isReply ? (
        <Reply
          wholeReplyMessageContent={matrixEvent}
          onQuote={handleSetQuotedMessage}
          onDelete={handleOpenDeleteConfirmation}
        />
      ) : (
        <MessageChipComponent
          content={matrixEvent}
          onQuote={handleSetQuotedMessage}
          onDelete={handleOpenDeleteConfirmation}
          eventPosition={eventPosition}
        />
      )}

      <ConfirmationDialog
        open={deleteConfirmationOpen}
        title={t('delete_message_title')}
        message={t('delete_message_dialog_content')}
        onConfirm={handleDeleteClick}
        onCancel={handleCloseDeleteConfirmation}
      />
    </>
  );
};

export default UserMatrixEvent;
